import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TimelineItem } from '../types';

const TIMELINE_ID_PARAM = 'timelineId';
const ACTIVITY_TAB_PARAM = 'activityTab';

export interface SelectedActivity {
    selectedTimelineId: string | null;
    selectedTimeline: TimelineItem | null;
    activityTab: string | null;
    openActivity: (id: string, tab?: string) => void;
    closeActivity: () => void;
    setActivityTab: (tab: string) => void;
}

export const useSelectedActivity = (
    timelineData: TimelineItem[],
    isLoading: boolean,
): SelectedActivity => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedTimelineId = searchParams.get(TIMELINE_ID_PARAM);
    const activityTab = searchParams.get(ACTIVITY_TAB_PARAM);

    const setSlideoutParams = useCallback((
        next: { timelineId: string; activityTab?: string } | null,
        options?: { replace?: boolean },
    ) => {
        setSearchParams((prev) => {
            const updated = new URLSearchParams(prev);

            updated.delete(TIMELINE_ID_PARAM);
            updated.delete(ACTIVITY_TAB_PARAM);

            if (next) {
                updated.set(TIMELINE_ID_PARAM, next.timelineId);
                if (next.activityTab) updated.set(ACTIVITY_TAB_PARAM, next.activityTab);
            }

            return updated;
        }, options);
    }, [setSearchParams]);

    const openActivity = (id: string, tab?: string) => setSlideoutParams({ 
        timelineId: id, 
        activityTab: tab 
    });

    const closeActivity = () => setSlideoutParams(null, { replace: true });

    const setActivityTab = (tab: string) => {
        if (!selectedTimelineId) return;
        setSlideoutParams({ timelineId: selectedTimelineId, activityTab: tab }, { replace: true });
    };

    const selectedTimeline = selectedTimelineId !== null
        ? timelineData.find(timeline => timeline.id === selectedTimelineId) ?? null
        : null;

    useEffect(() => {
        if (isLoading || selectedTimelineId === null) return;

        if (!timelineData.some(timeline => timeline.id === selectedTimelineId)) {
            setSlideoutParams(null, { replace: true });
        }
    }, [isLoading, selectedTimelineId, timelineData, setSlideoutParams]);

    return {
        selectedTimelineId, selectedTimeline, activityTab, openActivity, closeActivity, setActivityTab,
    };
};
